// init-success-rendering-and-helpers.ts
//
// Render success box sau khi init hoàn thành + các helper nhỏ dùng chung:
//   - resolveWorkspacePath (exported): resolve + handle conflict tên workspace
//   - promptTeamOwner: prompt email team owner với default từ SSO
//   - maybeCommitWorkspace: commit baseline workspace (trừ khi --no-commit)
//   - formatAiStatusLine: format dòng trạng thái AI setup trong success box
//   - formatGitnexusStatusLine: format dòng trạng thái GitNexus trong success box
//   - printInitSuccessBox: in box thành công + cheatsheet commands
//
// Tách ra để workspace-scaffold-and-finalize-orchestrator.ts + init-flow-handlers
// có thể import mà không tạo circular dependency với init.ts (entry point).

import { join, relative } from "node:path";
import { input, select } from "@inquirer/prompts";
import boxen from "boxen";
import { ensureDir, pathExists } from "../lib/filesystem-helpers.js";
import { formatPackCommandsCheatsheetBox } from "../lib/format-pack-commands-cheatsheet-box.js";
import { isPathInsideOrSameDirectory } from "../lib/is-path-inside-or-same-directory.js";
import { printArchitectureDiagram } from "../lib/print-architecture-diagram.js";
import { COMMANDS } from "../lib/print-welcome-screen.js";
import { UserAbortedRecoveryError } from "../lib/prompt-recovery-action-on-failure.js";
import type { runAiSetupPhase } from "../lib/run-ai-setup-phase.js";
import type { runGitnexusSetupPhase } from "../lib/run-gitnexus-setup-phase.js";
import { TEAM_PACK_RELATIVE_PATH } from "../lib/team-pack-manager.js";
import { chalk, log } from "../lib/terminal-logger.js";
import {
  findAlternativeWorkspaceName,
  isEmptyOrMissing,
} from "./init-conflict-detection-helpers.js";

// ─── shared utilities ───────

// v1.14.0 SECURITY: Path traversal guard. Workspace name từ user input (CLI flag
// `--workspace-name` hoặc inquirer prompt) → nếu chứa `..` / `/` / `\` → có thể
// resolve ngoài parent dir, ghi settings.json/gitignore vào path attacker-chosen.
// Block: name có separator hoặc resolve không bắt đầu bằng parent.
function assertWorkspaceNameSafe(parent: string, name: string): void {
  // Reject ngay name chứa path separator hoặc relative parts.
  // Whitelist khuyến nghị: alphanumeric, dash, underscore, dot — không có / \ ..
  if (/[/\\]/.test(name) || name === "." || name === ".." || name.includes("..")) {
    throw new Error(
      `Tên workspace không an toàn (chứa path separator hoặc '..'): "${name}".\nYêu cầu: chỉ chữ/số/dash/underscore/dot, không '/', '\\', '..'.`,
    );
  }
  // Defensive: resolve thực tế và assert vẫn nằm trong parent.
  // v3.19.3: dùng helper cross-platform. Bản cũ so prefix `${resolvedParent}/` →
  // hardcode separator POSIX nên chặn oan MỌI tên hợp lệ trên Windows (`\`), và
  // chặn oan cả trên POSIX khi parent là root `/`.
  if (!isPathInsideOrSameDirectory(parent, join(parent, name))) {
    throw new Error(`Tên workspace "${name}" resolve ra ngoài parent dir (path traversal). Block.`);
  }
}

export async function resolveWorkspacePath(
  parent: string,
  desiredName: string,
  force?: boolean,
): Promise<string> {
  // v1.14.0 SECURITY: assert ngay khi entry với name từ CLI flag.
  assertWorkspaceNameSafe(parent, desiredName);
  const desired = join(parent, desiredName);
  if (await isEmptyOrMissing(desired)) return desired;

  log.warn(`Workspace path "${desired}" đã có nội dung.`);

  // Loop cho user chọn alt name / nhập tay / abort.
  // v1.2.5: không throw thẳng khi findAlternative fail hoặc user decline alt.
  while (true) {
    const alternative = await findAlternativeWorkspaceName(parent, desiredName);

    if (force && alternative) {
      log.info(`--force: dùng ${alternative}`);
      return alternative;
    }

    const choices: Array<{ name: string; value: string }> = [];
    if (alternative) {
      choices.push({ name: `Dùng "${alternative}" (suggest)`, value: "use-alt" });
    }
    choices.push({ name: "Nhập tên workspace khác (manual)", value: "manual" });
    choices.push({ name: "Tạm ngưng init", value: "abort" });

    const action = await select({
      message: "Cách xử lý workspace path conflict?",
      choices,
    });

    if (action === "abort") {
      throw new UserAbortedRecoveryError(
        "User abort tại bước resolve workspace path. Chạy lại với --workspace-name khác.",
      );
    }
    if (action === "use-alt" && alternative) {
      return alternative;
    }
    // action === "manual" → prompt input tên mới + loop verify.
    // v1.14.0 SECURITY: inquirer validate reject path separator + .. ngay tại prompt.
    const newName = await input({
      message: "Tên workspace mới:",
      validate: (v) => {
        const trimmed = v.trim();
        if (trimmed.length === 0) return "Tên không được rỗng";
        if (
          /[/\\]/.test(trimmed) ||
          trimmed === "." ||
          trimmed === ".." ||
          trimmed.includes("..")
        ) {
          return "Tên không được chứa '/', '\\', '..' (path traversal)";
        }
        return true;
      },
    });
    // Defensive recheck — inquirer validate đã pass nhưng assert lần nữa cho consistency.
    assertWorkspaceNameSafe(parent, newName.trim());
    const newPath = join(parent, newName.trim());
    if (await isEmptyOrMissing(newPath)) return newPath;
    log.warn(`"${newPath}" cũng đã có nội dung. Thử tên khác.`);
    // Loop tiếp.
  }
}

export async function promptTeamOwner(currentUserEmail: string): Promise<string> {
  return await input({ message: "Team owner email:", default: currentUserEmail });
}

// v3: workspace KHÔNG git → maybeCommitWorkspace BỎ. Tạo workspace folder + báo lỗi
// rõ ràng nếu không tạo được (trước đây git().init() ngầm gánh việc này).
export async function ensureWorkspaceFolder(workspacePath: string): Promise<void> {
  try {
    await ensureDir(workspacePath);
  } catch (err) {
    const e = err as NodeJS.ErrnoException;
    if (e.code === "EACCES" || e.code === "EPERM") {
      throw new Error(`Không tạo được workspace "${workspacePath}": không có quyền ghi.`);
    }
    if (e.code === "ENAMETOOLONG") {
      throw new Error(`Đường dẫn workspace quá dài: "${workspacePath}".`);
    }
    if (e.code === "ENOSPC") {
      throw new Error("Hết dung lượng đĩa, không tạo được workspace.");
    }
    throw new Error(`Không tạo được workspace "${workspacePath}": ${e.message}`);
  }
}

// AI status line tùy theo kết quả runAiSetupPhase (hoặc null nếu --ai-skip).
export function formatAiStatusLine(
  aiResult: Awaited<ReturnType<typeof runAiSetupPhase>> | null,
): string {
  if (aiResult === null) {
    return `  ${chalk.yellow("AI:")} skipped · ${chalk.cyan("avatar ai setup")} để config sau`;
  }
  if (aiResult.ok) {
    const modelPart = aiResult.model ? ` · model=${aiResult.model}` : "";
    return `  ${chalk.green("AI:")} ready · ${aiResult.provider}${modelPart}`;
  }
  return `  ${chalk.yellow("AI:")} failed (${aiResult.reason.slice(0, 60)}) · thử ${chalk.cyan("avatar ai setup")}`;
}

// v1.4.0: GitNexus status line tương tự pattern formatAiStatusLine.
export function formatGitnexusStatusLine(
  result: Awaited<ReturnType<typeof runGitnexusSetupPhase>> | null,
): string {
  if (result === null) {
    return `  ${chalk.yellow("GitNexus:")} skipped · ${chalk.cyan("avatar gitnexus install")} để setup sau`;
  }
  if (result.ok) {
    const parts: string[] = ["ready"];
    if (result.analyzed) parts.push("indexed");
    if (result.wikiGenerated) parts.push("wiki");
    if (result.mcpRegistered) parts.push("mcp");
    return `  ${chalk.green("GitNexus:")} ${parts.join(" · ")}`;
  }
  return `  ${chalk.yellow("GitNexus:")} skipped (${(result.reason ?? "unknown").slice(0, 40)}) · thử ${chalk.cyan("avatar gitnexus install")}`;
}

// Render đầy đủ COMMANDS (nguồn dùng chung với welcome screen — DRY, tránh lệch
// danh sách lệnh giữa 2 nơi in ra). Align cột theo tên lệnh dài nhất.
function formatCommandListLines(): string[] {
  const maxNameWidth = Math.max(...COMMANDS.map((c) => c.name.length));
  return COMMANDS.map((c) => {
    const padded = c.name.padEnd(maxNameWidth);
    return `  ${chalk.cyan(`avatar ${padded}`)}   ${chalk.dim(c.desc)}`;
  });
}

export async function printInitSuccessBox(
  rootPath: string,
  aiResult: Awaited<ReturnType<typeof runAiSetupPhase>> | null = null,
  gitnexusResult: Awaited<ReturnType<typeof runGitnexusSetupPhase>> | null = null,
): Promise<void> {
  const lines: string[] = [
    `${chalk.green("✓")} Workspace sẵn sàng: ${relative(process.cwd(), rootPath) || rootPath}`,
    formatAiStatusLine(aiResult),
    formatGitnexusStatusLine(gitnexusResult),
    "",
    `  ${chalk.cyan(`cd ${rootPath}`)}`,
    `  ${chalk.cyan("claude")}                   Mở Claude Code ở workspace root`,
    "",
    chalk.bold(`${COMMANDS.length} lệnh sẵn sàng:`),
    "",
    ...formatCommandListLines(),
  ];
  process.stdout.write(`${boxen(lines.join("\n"), { padding: 1, borderStyle: "round" })}\n`);

  // Cheatsheet slash commands từ pack — ĐỌC ĐỘNG (v3.8.0) từ pack thật nên luôn
  // khớp, không lệch khi pack đổi command. Chỉ in khi pack có + đọc được lệnh.
  const packDir = join(rootPath, TEAM_PACK_RELATIVE_PATH);
  if (await pathExists(packDir)) {
    const cheatsheet = await formatPackCommandsCheatsheetBox(packDir);
    if (cheatsheet) process.stdout.write(`\n${cheatsheet}\n`);
  }

  // Chốt bằng sơ đồ kiến trúc 8 khối — cho user thấy "độ hoành tráng" + sự kết
  // nối của Avatar ngay lúc init xong (điểm chạm ấn tượng nhất).
  printArchitectureDiagram();
}
